class CollectionBannerParallax extends HTMLDivElement {
  constructor() {
    super();

    this.contentInner = this.querySelector(".section__content-inner");
    this.bannerMedia = this.querySelector(".banner__media");

    if (theme.params.isMotionReduced || (theme.params.isTouchDevice || theme.config.isSmallQuery)) {
      return;
    }

    this.initParallax();

    document.addEventListener("breakpoint:sm:match", this.unload.bind(this));
    document.addEventListener("breakpoint:sm:unmatch", this.initParallax.bind(this));

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:unload", () => {
        this.resetAnimation();
      });
    }
  }

  initParallax() {
    const animations = [];

    if (this.contentInner) {
      animations.push(
        Motion.scroll(
          Motion.animate(this.contentInner, {
            transform: ["scale(1) translateY(0)", "scale(0.5) translateY(200px)"],
            opacity: [1, -0.5]
          }), {
            target: this,
            offset: [
              [0, 0], [1, 0]
            ]
          })
      );
    }

    if (this.bannerMedia) {
      animations.push(
        Motion.scroll(
          Motion.animate(this.bannerMedia, {
            "--overlay-opacity": [parseFloat(getComputedStyle(this.bannerMedia).getPropertyValue("--overlay-opacity")) || 50, 150]
          }), {
            target: this,
            offset: [
              [0, 0], [1, 0]
            ]
          })
      );
    }

    this.animations = animations;
  }

  resetAnimation() {
    if (theme.params.isMotionReduced) {
      return;
    }

    setTimeout(() => {
      if (this.contentInner) {
        Motion.animate(this.contentInner, {
          transform: "scale(1) translateY(0)",
          opacity: 1
        }, {
          duration: 0
        });
      }

      if (this.bannerMedia) {
        Motion.animate(this.bannerMedia, {
          "--overlay-opacity": parseFloat(getComputedStyle(this.bannerMedia).getPropertyValue("--overlay-opacity")) || 50
        }, {
          duration: 0
        });
      }
    });
  }

  unload() {
    setTimeout(() => {
      this.animations?.forEach((animation) => {
        animation();
      });

      if (this.contentInner) {
        Motion.animate(this.contentInner, {
          transform: "translateY(0)",
          opacity: 1
        }, {
          duration: 0
        });
      }

      if (this.bannerMedia) {
        Motion.animate(this.bannerMedia, {
          "--overlay-opacity": parseFloat(getComputedStyle(this.bannerMedia).getPropertyValue("--overlay-opacity")) || 50
        }, {
          duration: 0
        });
      }
    });
  }
}

if (!window.customElements.get("collection-banner-parallax")) {
  customElements.define("collection-banner-parallax", CollectionBannerParallax, { extends: "div" });
}


class CollectionPage extends HTMLElement {
  constructor() {
    super();

    this.filterBlocks = document.querySelectorAll(".filter-blocks");
    this.productGridWrapper = this.querySelector(".product-grid-container");
    this.filterButton = this.querySelector(".collection__filter-button");
    this.collectionDropdownOptions = this.querySelectorAll(".collection-dropdown a");

    setTimeout(() => {
      this.productGridWrapper?.classList.add("visible");
    }, 500);

    this.moveFiltersDrawerOutOfSection();
    this.handleDropdownOptionsClick();

    if (this.filterButton) {
      this.setFilterButtonVisibility();
    }

    document.addEventListener("shopify:section:load", () => {
      this.moveFiltersDrawerOutOfSection();
    });

    this.handleScrollListener = theme.helpers.throttle(this.handleScroll.bind(this));
  }

  connectedCallback() {
    if (this.filterButton) {
      window.addEventListener("scroll", this.handleScrollListener, { passive: true });
    }
  }

  disconnectedCallback() {
    if (this.filterButton) {
      window.removeEventListener("scroll", this.handleScrollListener);
    }
  }

  handleScroll() {
    this.setFilterButtonVisibility();
  }

  handleDropdownOptionsClick() {
    this.collectionDropdownOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const triggerButton = option.closest(".collection-dropdown")?.querySelector(".disclosure__button");

        if (triggerButton) {
          triggerButton.classList.remove("open");
        }
      });
    });
  }

  setFilterButtonVisibility() {
    const scrollBottomPosition = window.scrollY + window.innerHeight;
    const sectionBottom = this.getBoundingClientRect().bottom + window.scrollY;

    const shouldBeVisible = scrollBottomPosition <= sectionBottom;

    if (this.filterButton.classList.contains("visible") !== shouldBeVisible) {
      this.filterButton.classList.toggle("visible", shouldBeVisible);
    }
  }

  moveFiltersDrawerOutOfSection() {
    const drawerWrapper = this.querySelector(".collection-drawer-wrapper");

    if (!drawerWrapper) {
      return;
    }

    const existingDrawer = document.body.querySelector(":scope > .collection-drawer-wrapper");

    if (existingDrawer) {
      existingDrawer.remove();
    }

    document.body.insertAdjacentElement("beforeend", drawerWrapper);
  }

  removeParam(key, sourceURL) {
    let rtn = sourceURL.split("?")[0],
        param,
        params_arr = [],
        queryString = sourceURL.indexOf("?") !== -1 ? sourceURL.split("?")[1] : "";

    if (queryString !== "") {
      params_arr = queryString.split("&");

      for (let i = params_arr.length - 1; i >= 0; i -= 1) {
        param = params_arr[i].split("=")[0];

        if (param === key) {
          params_arr.splice(i, 1);
        }
      }

      if (params_arr.length) {
        rtn = rtn + "?" + params_arr.join("&");
      }
    }

    return rtn;
  }

  renderUrl(searchParams) {
    return `${window.location.origin}${window.location.pathname}?section_id=${this.dataset.sectionId}&${searchParams}`;
  }

  renderSectionFilter(url, searchParams) {
    this.productGridWrapper = document.querySelector("collection-page .product-grid-container");
    this.productGridWrapper?.classList.add("product-grid-container--refreshing");

    // Nutraukiame ankstesnę filtro užklausą, jei vartotojas greitai
    // pažymi kelias reikšmes. Taip senesnis atsakymas neperrašo naujesnio.
    if (this.filterAbortController) {
      this.filterAbortController.abort();
    }

    this.filterAbortController = new AbortController();

    fetch(url, {
      signal: this.filterAbortController.signal,
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Collection filter request failed: ${response.status}`);
        }

        return response.text();
      })
      .then((responseText) => {
        const collectionPage = document.querySelector("collection-page");
        const drawer = document.querySelector("collection-drawer");
        const drawerFooter = drawer?.querySelector(".drawer__footer");

        const parsedHTML = new DOMParser().parseFromString(responseText, "text/html");
        const filterBlocks = parsedHTML.querySelector(".filter-blocks");
        const promotions = parsedHTML.querySelectorAll(".grid__item--promotion");
        const newSection = parsedHTML.querySelector("collection-page");

        if (!collectionPage || !newSection) {
          throw new Error("Collection page markup was not found in the filtered response.");
        }

        const newDrawer = newSection.querySelector(".collection-drawer-wrapper");
        const newDrawerFooter = newSection.querySelector(".collection-drawer-wrapper .drawer__footer");

        if (drawerFooter && newDrawerFooter) {
          drawerFooter.replaceWith(newDrawerFooter);
          document.dispatchEvent(new CustomEvent("drawer:update-close-buttons"));
        }

        if (newDrawer) {
          newDrawer.remove();
        }

        promotions.forEach((promotion) => {
          promotion.remove();
        });

        // Atnaujiname visus filtro blokus, tačiau tik jei jie egzistuoja atsakyme.
        if (filterBlocks) {
          document.querySelectorAll(".filter-blocks").forEach((filterBlock) => {
            filterBlock.innerHTML = filterBlocks.innerHTML;
          });
        }

        collectionPage.replaceWith(newSection);

        const productsList =
          document.querySelector(".collection-drawer-wrapper .drawer.open") ||
          newSection.querySelector(".collection-sidebar");

        const focusableElement = productsList?.querySelector("a, input");
        focusableElement?.focus({ preventScroll: true });

        document.dispatchEvent(new CustomEvent("hover-button:apply"));
        document.dispatchEvent(new CustomEvent("theme:header:add-spacing"));
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return;
        }

        console.error(error);
        this.productGridWrapper?.classList.remove("product-grid-container--refreshing");
      });

    this.updateUrl(url, searchParams);
  }

  updateUrl(url, searchParams = false) {
    const newUrl = this.updateURLHash(url);

    if (searchParams) {
      const urlUpdate = `${window.location.pathname}?${searchParams}`;

      window.history.pushState({url: urlUpdate}, "", urlUpdate);
    } else {
      window.history.pushState({url: newUrl}, "", newUrl);
    }
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);

    clonedUrl.searchParams.delete("section_id");

    return clonedUrl.toString();
  }
}

if (!window.customElements.get("collection-page")) {
  customElements.define("collection-page", CollectionPage);
}


class CollectionDrawer extends Drawer {
  constructor() {
    super();
  }
}

if (!window.customElements.get("collection-drawer")) {
  customElements.define("collection-drawer", CollectionDrawer);
}


class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");

    this.minRange = this.querySelector(".range-slider-min");
    this.maxRange = this.querySelector(".range-slider-max");

    this.minPrice = this.querySelector(".min-price-range");
    this.maxPrice = this.querySelector(".max-price-range");

    this.init();
  }

  init() {
    this.minRange.addEventListener("change", (event) => {
      event.preventDefault();

      this.minRange.value = this.minRange.value.replace(",", "");

      const min = +this.minRange.min;
      const max = +this.minRange.max;
      const value = +this.minRange.value;

      if (min + value >= 0) {
        if (value > max) {
          this.minPrice.value = max;
        } else {
          this.minPrice.value = value;
        }
      } else {
        this.minPrice.value = min;
      }
    }, false);

    this.maxRange.addEventListener("change", (event) => {
      event.preventDefault();

      this.maxRange.value = this.maxRange.value.replace(",", "");

      const max = +this.maxRange.max;
      const value = +this.maxRange.value;

      if (max - value >= 0) {
        this.maxPrice.value = value;
      } else {
        this.maxPrice.value = max;
      }
    }, false);

    this.minRange.addEventListener("input", (event) => {
      event.preventDefault();

      const min = +this.minRange.value;
      const max = +this.maxRange.value;

      if (max - min >= 1) {
        this.minPrice.value = min;
      } else {
        this.minRange.value = max - 1;
      }

      this.minRange.closest(".price-range").style.setProperty("--range-from", (this.minRange.value / Number(this.minRange.getAttribute("max"))) * 100 + "%");
    }, false);

    this.maxRange.addEventListener("input", (event) => {
      event.preventDefault();

      const min = +this.minRange.value;
      const max = +this.maxRange.value;

      if (max - min >= 1) {
        this.maxPrice.value = max;
      } else {
        this.maxRange.value = min + 1;
      }

      this.maxRange.closest(".price-range").style.setProperty("--range-to", (this.maxRange.value / Number(this.maxRange.getAttribute("max"))) * 100 + "%");
    }, false);

    this.minPrice.addEventListener("input", theme.helpers.debounce((event) => {
      event.preventDefault();

      this.minPrice.value = this.minPrice.value.replace(",", "");

      const min = +this.minPrice.min;
      const max = +this.maxRange.value;
      const value = +this.minPrice.value;

      if (max - value >= 1) {
        if (value >= 0) {
          this.minPrice.value = value;
        } else {
          this.minPrice.value = min;
        }
      } else {
        this.minPrice.value = max - 1;
      }
    }, 500));

    this.maxPrice.addEventListener("input", theme.helpers.debounce((event) => {
      event.preventDefault();

      this.maxPrice.value = this.maxPrice.value.replace(",", "");

      const min = +this.minRange.value;
      const max = +this.maxRange.max;
      const value = +this.maxPrice.value;

      if (max - value >= 0) {
        if (value - min >= 1) {
          this.maxPrice.value = value;
        } else {
          this.maxPrice.value = min + 1;
        }
      } else {
        this.maxPrice.value = max;
      }
    }, 500));
  }
}

if (!window.customElements.get("price-range")) {
  customElements.define("price-range", PriceRange);
}


class FacetsForm extends HTMLFormElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");
    this.swatches = this.querySelectorAll(".swatch-option-input");
    this.sortSelect = this.querySelector("select[name='sort_by']");

    // Checkboxai ir select filtrai pritaikomi beveik iškart.
    // Kainos laukams paliekamas trumpas debounce, kad užklausa nebūtų
    // siunčiama po kiekvieno įvesto skaitmens.
    this.handleChangeListener = this.handleChange.bind(this);
    this.handleSwatchClickListener = this.handleSwatchClick.bind(this);
    this.priceChangeTimer = null;
  }

  connectedCallback() {
    this.addEventListener("change", this.handleChangeListener);

    this.swatches.forEach((swatch) => {
      swatch.addEventListener("change", this.handleSwatchClickListener);
    });
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.handleChangeListener);

    this.swatches.forEach((swatch) => {
      swatch.removeEventListener("change", this.handleSwatchClickListener);
    });

    clearTimeout(this.priceChangeTimer);
  }

  handleSwatchClick(evt) {
    evt.target.nextElementSibling?.classList.toggle("current-filter");
  }

  handleChange(event) {
    this.collectionPage = document.querySelector("collection-page");

    if (!this.collectionPage) {
      return;
    }

    const target = event?.target;
    const isPriceInput =
      target?.matches?.(
        "input[name='filter.v.price.gte'], input[name='filter.v.price.lte'], .min-price-range, .max-price-range, .range-slider-min, .range-slider-max"
      );

    clearTimeout(this.priceChangeTimer);

    if (isPriceInput) {
      this.priceChangeTimer = setTimeout(() => {
        this.applyFilters();
      }, 450);

      return;
    }

    this.applyFilters();
  }

  applyFilters() {
    this.collectionPage = document.querySelector("collection-page");

    if (!this.collectionPage) {
      return;
    }

    const url = this.buildUrl().toString();
    this.collectionPage.renderSectionFilter(url);
  }

  buildUrl() {
    const currentParams = new URLSearchParams(window.location.search);
    const searchParam = currentParams.get("q");

    // Vertikaliame filtre dažnai nėra sort select elemento.
    // Ankstesnis this.sortSelect.value tokiu atveju išmesdavo JS klaidą
    // ir checkbox filtravimas visiškai sustodavo.
    const sortValue =
      this.sortSelect?.value ||
      currentParams.get("sort_by") ||
      this.collectionPage?.querySelector("[is='filter-sort'][selected]")?.getAttribute("data-value");

    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action || window.location.href, window.location.origin);

    url.search = "";

    searchParams.forEach((value, key) => {
      if (value !== "") {
        url.searchParams.append(key, value);
      }
    });

    ["page", "filter.v.price.gte", "filter.v.price.lte"].forEach((item) => {
      if (url.searchParams.get(item) === "") {
        url.searchParams.delete(item);
      }
    });

    if (this.collectionPage?.dataset.sectionId) {
      url.searchParams.set("section_id", this.collectionPage.dataset.sectionId);
    }

    if (sortValue) {
      url.searchParams.set("sort_by", sortValue);
    }

    if (searchParam) {
      url.searchParams.set("q", searchParam);
    }

    return url;
  }
}

if (!window.customElements.get("facets-form")) {
  customElements.define("facets-form", FacetsForm, { extends: "form" });
}


class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");

    this.handleClickListener = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClickListener);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClickListener);
  }

  handleClick(evt) {
    const searchParam = new URLSearchParams(window.location.search).get("q");
    const sortValue = this.collectionPage.querySelector("[is='filter-sort'][selected]")?.getAttribute("data-value");
    const form = this.closest('form[is="facets-form"]') || document.querySelector('form[is="facets-form"]');

    if (form) {
      evt.preventDefault();

      const url = new URL(this.href);

      url.searchParams.set("section_id", this.collectionPage.dataset.sectionId);

      if (sortValue) {
        url.searchParams.set("sort_by", sortValue);
      }

      if (searchParam) {
        url.searchParams.set("q", searchParam);
      }

      this.collectionPage.renderSectionFilter(url.toString());
    }
  }
}

if (!window.customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemove, { extends: "a" });
}


class FilterSort extends HTMLAnchorElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");

    this.addEventListener("click", this.filterSort.bind(this), false);
  }

  filterSort(evt) {
    evt.preventDefault();

    const value = this.getAttribute("data-value");
    let queryString = window.location.search;

    queryString = this.collectionPage.removeParam("sort_by", queryString);

    const searchParams = queryString.replace("?", "") + "&sort_by=" + value;
    const url = this.collectionPage.renderUrl(searchParams);

    this.collectionPage.scrollIntoView({ behavior: "smooth" })

    this.collectionPage.renderSectionFilter(url, searchParams);
  }
}

if (!window.customElements.get("filter-sort")) {
  customElements.define("filter-sort", FilterSort, { extends: "a" });
}


class CollectionFiltersToggle extends HTMLButtonElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");

    this.openText = this.getAttribute("open-text");
    this.closeText = this.getAttribute("close-text");

    this.textContainer = this.querySelector("[data-toggle-text]");

    this.addEventListener("click", this.toggleVerticalFilter.bind(this));
  }

  toggleVerticalFilter() {
    if (this.collectionPage.classList.contains("vertical-filter-hidden")) {
      this.collectionPage.classList.remove("vertical-filter-hidden");
      this.textContainer.innerText = this.closeText;
      this.setAttribute("aria-label", this.closeText);
    } else {
      this.collectionPage.classList.add("vertical-filter-hidden");
      this.textContainer.innerText = this.openText;
      this.setAttribute("aria-label", this.openText);
    }
  }
}

if (!window.customElements.get("collection-filters-toggle")) {
  customElements.define("collection-filters-toggle", CollectionFiltersToggle, { extends: "button" });
}


class GridSwitcher extends HTMLElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");
    this.productGridWrapper = this.collectionPage.querySelector(".product-grid-container");
    this.currentSwitchers = this.querySelectorAll(".grid-switcher__current-wrapper [data-switcher]");
    this.switchers = this.querySelectorAll(".grid-switcher__switchers [data-switcher]");

    this.handleSwitcherClickListener = this.handleSwitcherClick.bind(this);
  }

  connectedCallback() {
    this.switchers.forEach((switcher) => {
      switcher.addEventListener("click", this.handleSwitcherClickListener, false);
    });
  }

  disconnectedCallback() {
    this.switchers.forEach((switcher) => {
      switcher.removeEventListener("click", this.handleSwitcherClickListener, false);
    });
  }

  handleSwitcherClick(event) {
    const target = event.target.closest("[data-switcher]");
    const mode = target.dataset.mode;
    const containerSwitch = document.querySelector(".container-products-switch");
    const containerSwitchMode = containerSwitch.getAttribute("data-view-mode");

    this.productGridWrapper.classList.add("product-grid-container--refreshing");

    setTimeout(() => {
      this.productGridWrapper.classList.add("animated-blocks--hide-instantly");
      this.productGridWrapper.classList.remove("visible");
      this.productGridWrapper.classList.remove("product-grid-container--refreshing");
    }, 300);

    this.setActiveSwitcher(mode);

    setTimeout(() => {
      containerSwitch.classList.remove(`grid-columns-${containerSwitchMode}`);
      containerSwitch.classList.add(`grid-columns-${mode}`);
      containerSwitch.setAttribute("data-view-mode", mode);

      this.productGridWrapper.classList.remove("animated-blocks--hide-instantly");
      this.productGridWrapper.classList.add("visible");
    }, 350);
  }

  setActiveSwitcher(mode) {
    this.currentSwitchers.forEach((switcher) => {
      if (mode === switcher.dataset.mode) {
        switcher.classList.remove("!hidden");
      } else {
        switcher.classList.add("!hidden");
      }
    });
  }
}

if (!window.customElements.get("grid-switcher")) {
  customElements.define("grid-switcher", GridSwitcher);
}


class SubCollections extends HTMLElement {
  constructor() {
    super();

    this.collectionPage = document.querySelector("collection-page");
    this.stickyHeader = document.querySelector(".header-section--sticky-always, .header-section--sticky-on-scroll-up");

    this.setSubcollectionsPosition();

    this.handleScrollListener = theme.helpers.throttle(this.handleScroll.bind(this));
  }

  connectedCallback() {
    window.addEventListener("scroll", this.handleScrollListener, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScrollListener);
  }

  handleScroll() {
    this.setSubcollectionsPosition();
  }

  setSubcollectionsPosition() {
    const headerOffset = this.stickyHeader ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0 : 0;
    const shouldBeVisible = this.collectionPage.getBoundingClientRect().top - headerOffset < 0;

    if (this.classList.contains("visible") !== shouldBeVisible) {
      this.classList.toggle("visible", shouldBeVisible);
    }
  }
}

if (!window.customElements.get("sub-collections")) {
  customElements.define("sub-collections", SubCollections);
}